/**
 * Restore wallet orchestration hook.
 *
 * Manages the state machine for restoring a wallet from a 12-word
 * recovery phrase. Handles: validation, BIP-85 derivation, Evolu sync,
 * Nostr keypair derivation, new PIN setup, and mnemonic encryption.
 *
 * State machine: idle -> validating -> syncing -> pin_setup -> encrypting -> restoring_tokens -> complete
 *
 * Tier 1 component - used during wallet restore flow.
 *
 * @module useRestoreWallet
 */

import { useCallback, useState } from 'react';
import { useEvolu } from '@evolu/react';
import type { Evolu } from '@evolu/common';
import { NonEmptyString1000, getOrThrow } from '@evolu/common';
import { validateMnemonic, deriveChildMnemonic, deriveNostrKeypair } from '@/lib/crypto';
import { encryptMnemonic } from '@/lib/crypto-encrypt';
import type { SatsflowSchema } from '@/db/database';
import { createPlatformServices } from '@/lib/platform/index';
import { useMnemonic } from '@/contexts/MnemonicProvider';
import { CONFIG } from '@/lib/config';
import { seedNostrIdentity } from '../utils/wallet-seed-helpers';
import {
  restoreTokensFromMints,
  type MintRestoreProgress,
  type TokenRestoreResult,
} from '@/lib/cashu-restore';

/**
 * Module-level mnemonic storage for the restore flow.
 *
 * SECURITY: Held in memory only during the restore flow, cleared after use.
 *
 * Why module-level instead of useRef:
 * Evolu's restoreAppOwner can cause the EvoluProvider to remount its
 * entire component subtree. When that happens, the useRestoreWallet hook
 * is destroyed and recreated, resetting any useRef to null — which loses
 * the mnemonic between the syncing and pin_setup steps. A module-level
 * variable survives component remounts.
 */
let pendingMnemonic: string | null = null;

/**
 * Reset module-level state. For testing only.
 * @internal
 */
export function resetPendingMnemonic(): void {
  pendingMnemonic = null;
}

/**
 * Cast the generic Evolu instance to the app's typed schema.
 * Same pattern used in useCreateWallet.
 */
function typedEvolu(evolu: ReturnType<typeof useEvolu>): Evolu<SatsflowSchema> {
  return evolu as unknown as Evolu<SatsflowSchema>;
}

/** Possible states in the restore flow. */
export type RestoreState =
  | 'idle'
  | 'validating'
  | 'syncing'
  | 'pin_setup'
  | 'encrypting'
  | 'restoring_tokens'
  | 'complete';

/** SecureStorage key for the encrypted master mnemonic. */
const ENCRYPTED_MNEMONIC_KEY = 'encrypted_mnemonic';

/** Sync timeout before showing "taking a moment" message (ms). */
const SYNC_SLOW_THRESHOLD = 10_000;

/** Sync timeout before offering manual continue (ms). */
const SYNC_MAX_TIMEOUT = 30_000;

/** Return value of the useRestoreWallet hook. */
export interface UseRestoreWalletReturn {
  /** Current state in the restore flow. */
  readonly state: RestoreState;
  /** Error message if any step fails. */
  readonly error: string | null;
  /** Whether sync is taking longer than expected. */
  readonly syncSlow: boolean;
  /** Per-mint token restore progress (updated during restoring_tokens state). */
  readonly tokenRestoreProgress: MintRestoreProgress[];
  /** Final result of token restore, or null if not yet complete. */
  readonly tokenRestoreResult: TokenRestoreResult | null;
  /** Begin restore with a validated mnemonic. */
  startRestore(mnemonic: string): void;
  /** Set new PIN and finalize the restore (encrypt + store). */
  setPinAndFinalize(pin: string): void;
  /** Skip waiting for sync and continue to PIN setup. */
  skipSync(): void;
}

/**
 * Hook that orchestrates the wallet restore flow.
 *
 * @param restoreEvolu - Function to restore Evolu with a child mnemonic. Injected for testability.
 * @returns State machine controls for the restore flow.
 */
export function useRestoreWallet(
  restoreEvolu: (childMnemonic: string) => Promise<void>,
): UseRestoreWalletReturn {
  // If pendingMnemonic exists on mount, the component remounted mid-restore
  // (e.g., Evolu's restoreAppOwner caused a tree remount). Skip straight to PIN setup.
  const [state, setState] = useState<RestoreState>(pendingMnemonic ? 'pin_setup' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const [syncSlow, setSyncSlow] = useState(false);
  const [tokenRestoreProgress, setTokenRestoreProgress] = useState<MintRestoreProgress[]>([]);
  const [tokenRestoreResult, setTokenRestoreResult] = useState<TokenRestoreResult | null>(null);
  const { unlock } = useMnemonic();
  const evolu = useEvolu();

  const startRestore = useCallback(
    (mnemonic: string): void => {
      if (state !== 'idle') return;

      setState('validating');
      setError(null);

      if (!validateMnemonic(mnemonic)) {
        setError('Invalid recovery phrase. Please check your words and try again.');
        setState('idle');
        return;
      }

      pendingMnemonic = mnemonic;
      setState('syncing');

      // Derive keys before starting async restore — catch derivation errors
      let childMnemonic: string;
      try {
        childMnemonic = deriveChildMnemonic(mnemonic, 0);
        // Derive Nostr keypair (index 1) — verify derivation succeeds before proceeding
        deriveNostrKeypair(mnemonic);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to derive wallet keys';
        pendingMnemonic = null;
        setError(message);
        setState('idle');
        return;
      }

      // Track sync timing
      const slowTimeout = setTimeout(() => {
        setSyncSlow(true);
      }, SYNC_SLOW_THRESHOLD);

      const maxTimeout = setTimeout(() => {
        // After max timeout, auto-advance to PIN setup
        clearTimeout(slowTimeout);
        setState('pin_setup');
      }, SYNC_MAX_TIMEOUT);

      void restoreEvolu(childMnemonic)
        .then(() => {
          clearTimeout(slowTimeout);
          clearTimeout(maxTimeout);
          setSyncSlow(false);
          setState('pin_setup');
        })
        .catch((err: unknown) => {
          clearTimeout(slowTimeout);
          clearTimeout(maxTimeout);
          setSyncSlow(false);
          const message = err instanceof Error ? err.message : 'Failed to sync wallet data';
          pendingMnemonic = null;
          setError(message);
          setState('idle');
        });
    },
    [state, restoreEvolu],
  );

  const skipSync = useCallback((): void => {
    if (state !== 'syncing') return;
    setSyncSlow(false);
    setState('pin_setup');
  }, [state]);

  const setPinAndFinalize = useCallback(
    (pin: string): void => {
      if (state !== 'pin_setup') return;
      if (!pendingMnemonic) {
        // Finalization already completed on a prior call (e.g., React strict
        // mode or PinSetup double-firing). Silently ignore the duplicate.
        return;
      }

      setState('encrypting');
      const mnemonic = pendingMnemonic;

      void (async (): Promise<void> => {
        try {
          // Encrypt mnemonic with new PIN
          const encrypted = await encryptMnemonic(mnemonic, pin);

          // Store in SecureStorage
          const services = await createPlatformServices();
          await services.secureStorage.store(ENCRYPTED_MNEMONIC_KEY, encrypted);

          // Set backup_completed_at in settings (restore = already backed up).
          // A restored wallet already has a backup — suppress backup reminders.
          typedEvolu(evolu).insert('setting', {
            key: getOrThrow(NonEmptyString1000.from('backup_completed_at')),
            value: getOrThrow(NonEmptyString1000.from(new Date().toISOString())),
          });

          // Re-derive and store Nostr identity from the master mnemonic.
          // During wallet creation, this is done in seedNostrIdentity.
          // On restore, Evolu sync may not bring the userIdentity row
          // (relay may be incomplete), so we re-derive it deterministically.
          seedNostrIdentity(evolu, mnemonic);

          // NUT-09 token recovery: scan mints for recoverable tokens
          setState('restoring_tokens');
          const mints = await getConnectedMints(typedEvolu(evolu));
          if (mints.length > 0) {
            const result = await restoreTokensFromMints({
              mnemonic,
              mints,
              evolu: typedEvolu(evolu),
              onProgress: (progress) => setTokenRestoreProgress(progress),
            });
            setTokenRestoreResult(result);
          }

          // TODO: NWC reconnection
          // After restore, prompt user to re-enter NWC connection strings.
          // Previous connection secrets may not sync if the user's Alby Hub
          // was reconfigured.

          // Unlock mnemonic in memory for session
          unlock(mnemonic);

          // Clear module-level storage (security: don't hold mnemonic longer than needed)
          pendingMnemonic = null;

          setState('complete');
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to encrypt and store wallet';
          setError(message);
          setState('pin_setup');
        }
      })();
    },
    [state, unlock, evolu],
  );

  return {
    state,
    error,
    syncSlow,
    tokenRestoreProgress,
    tokenRestoreResult,
    startRestore,
    setPinAndFinalize,
    skipSync,
  };
}

/** Default mint URL used when Evolu has no mints synced. */
const DEFAULT_MINT_URL = CONFIG.defaultMintUrl;

/**
 * Query connected mints from Evolu, with fallback to default mint.
 *
 * @param evolu - Typed Evolu instance
 * @returns Array of mint info objects for token restore
 */
async function getConnectedMints(
  evolu: Evolu<SatsflowSchema>,
): Promise<Array<{ url: string; label: string; mintId: string }>> {
  try {
    const query = evolu.createQuery((db) =>
      db
        .selectFrom('cashuMint')
        .select(['id', 'url', 'label', 'isActive'])
        .where('isActive', '=', 1 as never),
    );
    const rows = await evolu.loadQuery(query);

    if (rows.length > 0) {
      return rows.map((row) => ({
        url: String(row.url),
        label: String(row.label),
        mintId: String(row.id),
      }));
    }
  } catch {
    // Evolu query may fail if sync hasn't completed; fall through to default
  }

  return [{ url: DEFAULT_MINT_URL, label: CONFIG.defaultMintLabel, mintId: '' }];
}
